import { Injectable, Input } from '@angular/core';
import { Store } from '@ngrx/store';
import { iif, Observable, of, Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, filter, first, map, skipWhile, switchMap } from 'rxjs/operators';

import {
  DetalleProductos,
  IProductos,
  IPromosEcommerce,
  ISummaryPrice,
  ISummaryPricePromo
} from '../../../private/shared/models/productos';
import { AppReducerState, AppState } from '../../../store/app-reducers';
import { CartProductsState } from '../../../store/reducers';
import { SummaryPriceDefault } from '../../models/Constants';

@Injectable({
  providedIn: 'root'
})
export class OperationsService {

  @Input() LimitMinMonto: number | null = 0;
  @Input() LimitMinCajas: number | null = 0;
  @Input() LimitMaxMonto: number | null = 0;
  @Input() LimitMaxCajas: number | null = 0;
  @Input() LimiteMinMontoAdvertencia: number | null = 0;
  @Input() Modelo: number = 0;

  private allCoincidences = new Subject<string>();
  private onProductSelect = new Subject<IProductos>();

  constructor(
    private store: Store<AppState>
  ) { }

  public allCoincidences$ = this.allCoincidences.asObservable();

  public onProductSelect$ = this.onProductSelect.asObservable();

  public allCoincidencesAction = (search: string) => this.allCoincidences.next(search);

  public onProductSelectAction = (product: IProductos) => this.onProductSelect.next(product);

  public ramdonNumber = (minimo: number, maximo: number) => Math.round(Math.random() * (maximo - minimo) + minimo);

  /**
   * Formatea a dos decimales el valor
   * @param number valor a reducir a dos decimales
   */
  public twoDecimal = (numero: number) => Math.round((numero + Number.EPSILON) * 100) / 100;

  /**
   * Fucionar carrito agregado con pedido realizado
   */
  public combinerCartOrder$ = () => this.store.select('cartProducts')
    .pipe(
      debounceTime(500),
      distinctUntilChanged((prev, curr) => this.deepEqual(prev, curr)),
      map<CartProductsState, CartProductsState>(cart => {
        let actualPrts = cart.cartProductos.filter(p => !p.isCarrito).map(p => ({ ...p, isEdit: false, isNew: false, }));
        let actualPrtsPromos = cart.cartPromos.filter(p => !p.isCarrito).map(p => ({ ...p, isEdit: false, isNew: false, }));
        let actualPromo = cart.promo.filter(p => !p.isCarrito).map(p => ({ ...p, isEdit: false, isNew: false, }));

        const cartProductos = [...cart.cartProductos.filter(p => p.isCarrito).map(p => ({ ...p, isNew: true, isEdit: false, }))];
        const cartPromos = [...cart.cartPromos.filter(p => p.isCarrito).map(p => ({ ...p, isNew: true, isEdit: false, }))];
        const cartPromo = [...cart.promo.filter(p => p.isCarrito).map(p => ({ ...p, isNew: true, isEdit: false, }))];

        actualPrts = actualPrts.map(ap => {
          const index = cartProductos.findIndex(x => x.id_producto === ap.id_producto);
          if (index >= 0) {
            const apu = {
              ...ap,
              productos: cartProductos[index].productos,
              isEdit: true,
              newAdd: (cartProductos[index].productos || 0) - (ap.productos || 0),
              isNew: false,
            };
            cartProductos.splice(index, 1);
            return apu;
          } else {
            return ap;
          }
        }).filter(ap => (ap.productos || 0) > 0);

        actualPrtsPromos = actualPrtsPromos.map(ap => {
          const index = cartPromos.findIndex(x => x.id_producto === ap.id_producto && x.ClavePromo === ap.ClavePromo && x.Nivel === ap.Nivel);
          if (index >= 0) {
            const apu = {
              ...ap,
              productos: cartPromos[index].productos,
              isEdit: true,
              newAdd: (cartPromos[index].productos || 0) - (ap.productos || 0),
              isNew: false,
            };
            cartPromos.splice(index, 1);
            return apu;
          } else {
            return ap;
          }
        }).filter(ap => (ap.productos || 0) > 0);

        actualPromo = actualPromo.map(ap => {
          const index = cartPromo.findIndex(x => x.ClavePromo === ap.ClavePromo);
          if (index >= 0) {
            const apu = {
              ...ap,
              PaquetesCanjeados: cartPromo[index].PaquetesCanjeados,
              isEdit: true,
              newAdd: cartPromo[index].PaquetesCanjeados - ap.PaquetesCanjeados,
              isNew: false,
            };
            cartPromo.splice(index, 1);
            return apu;
          } else {
            return ap;
          }
        }).filter(ap => ap.PaquetesCanjeados > 0);

        const cartPromosProducts = actualPrtsPromos.concat(cartPromos);
        const promo = actualPromo.concat(cartPromo);
        if (promo.some(p => p.Bonificacion && p.CartAuto)) {
          const canje = promo.filter(p => p.Bonificacion && p.CartAuto)[0];
          promo.map(p => p.Bonificacion ? ({ ...p, PaquetesCanjeados: 1 }) : p);
          cartPromosProducts.map(p => p.ClavePromo === canje.ClavePromo ? ({ ...p, productos: (p.productos || 0) / canje.PaquetesCanjeados }) : p);
        }

        return {
          ...cart,
          cartProductos: actualPrts.concat(cartProductos),
          cartPromos: cartPromosProducts,
          promo,
        };
      }),
      switchMap(cart => this.tienda$.pipe(
        map(tienda => ({
          ...cart,
          promo: cart.promo.map(cp => {
            const prm = tienda.data.promos.filter(pr => pr.ClavePromo === cp.ClavePromo)[0];
            return { ...prm, ...cp, MaximoPaquetes: prm.MaximoPaquetes };
          }),
          cartProductos: cart.cartProductos.map(cp => {
            const pdt = tienda.data.categorias.products.filter(p => p.clave_producto === cp.clave_producto)[0];
            return { ...cp, puntos_recompepsi: pdt?.puntos_recompepsi || 0, portafolio: pdt?.portafolio || false, impacto_recompra: pdt?.impacto_recompra || 0 };
          }),
          cartPromos: cart.cartPromos.map(cp => {
            const prm = tienda.data.promos.filter(p => p.ClavePromo === cp.ClavePromo)[0];
            const prdP = prm.Productos.filter(pr => pr.clave_producto === cp.clave_producto && pr.ClavePromo === cp.ClavePromo && (prm.TipoPromocion !== 'ESCALERA' ? pr.Nivel === cp.Nivel : true))[0];
            return { ...cp, puntos_recompepsi: (prm?.puntos_recompepsi || 0) > 0 ? prm.puntos_recompepsi : prdP?.puntos_recompepsi || 0 };
          })
        })
        )
      )),
      map<CartProductsState, CartProductsState>(cart => ({ ...cart, summaryPrice: this.summaryPrice(cart) }))
    );

  private deepEqual = (obj1: any, obj2: any): boolean => JSON.stringify(obj1) === JSON.stringify(obj2);

  private tienda$ = this.store.select('GetmTienda')
    .pipe(
      filter(x => x.success || x.failed),
      first(),
    );

  public summaryCartPrevious$ = (state: AppReducerState): Observable<ISummaryPrice> => this.store.select(state)
    .pipe(
      map((cart: any) => {
        const cartProductos = (cart.cartProductos as IProductos[]).filter(p => p.hasOwnProperty('isCarrito') && !p.isCarrito);
        const cartPromos = (cart.cartPromos as IProductos[]).filter(p => p.hasOwnProperty('isCarrito') && !p.isCarrito);
        const totalProducts = cartProductos.concat(cartPromos);
        return totalProducts.length < 1 ? SummaryPriceDefault :
          totalProducts.map<ISummaryPrice>(produc => ({
            total_cajas: produc.productos || 0,
            total_precio: produc.price_info || (produc.precio || 0) * (produc.productos || 0),
            precio_descuento: produc.precio_original ? (produc.precio_original || 0) * (produc.productos || 0) - (produc.price_info || (produc.precio || 0) * (produc.productos || 0)) : 0,
            puntos_recompepsi: (produc.puntos_recompepsi || 0) * (produc.productos || 0)
          }))
            .map(values => ({
              total_cajas: values.total_cajas,
              total_precio: this.twoDecimal(values.total_precio),
              precio_descuento: this.twoDecimal(values.precio_descuento),
              puntos_recompepsi: values.puntos_recompepsi,
            }))
            .reduce((a, b) => ({
              total_cajas: a.total_cajas + b.total_cajas,
              total_precio: a.total_precio + b.total_precio,
              precio_descuento: a.precio_descuento + b.precio_descuento,
              puntos_recompepsi: a.puntos_recompepsi + b.puntos_recompepsi
            }));
      }),
    )

  /**
   * Formatea datos del pedido actual y carrito agregado
   */
  public formatResult = (element: DetalleProductos, isCarrito: boolean): IProductos => ({
    id_producto: element.id_producto,
    title: element.title,
    subtitle: element.subtitle,
    imagen: element.imagen,
    precio: element.unit_price,
    price_info: element.price_info,
    productos: element.quantity,
    sku: element.flavors,
    clave_producto: element.ClaveProducto,
    ClavePromo: element.ClavePromo,
    precio_original: element.precio_original,
    Minimo: element.Minimo,
    Maximo: element.Maximo,
    Nivel: element.Nivel,
    modelo: element.modelo,
    marca: element.marca,
    segmento: element.segmento,
    familia: element.familia,
    nombre_producto: element.nombre_producto,
    presentacion: element.presentacion,
    FactorCl: element.FactorCl,
    isCarrito,
    TipoEnvase: element.TipoEnvase,
    Etiquetados: element.Etiquetados,
    Multiplicador: element.Multiplicador,
    KeyEtiquetados: element.KeyEtiquetados,
    TotalEnCaja: element.TotalEnCaja,
    Retornabilidad: element.Retornabilidad,
    discount_type: element.hasOwnProperty('discount_type') ? element.discount_type : null,
    discType: element.hasOwnProperty('discType') ? element.discType : null,
    discId: element.hasOwnProperty('discId') ? element.discId : null,
    discLevel: element.hasOwnProperty('discLevel') ? element.discLevel : null,
    price_discount: element.hasOwnProperty('price_discount') ? this.twoDecimal(element.price_discount || 0) : null,
  }) as IProductos;

  /**
   * Realiza la suma del total de precios y cajas del carrito
   */
  public summaryPriceTotal$ = (): Observable<ISummaryPrice> => this.combinerCartOrder$()
    .pipe(
      switchMap(cart =>
        iif<CartProductsState, CartProductsState>(() => cart.promo.length < 1,
          of(cart),
          this.store.select('promosEcommerce')
            .pipe(
              skipWhile(x => !x.success && !x.failed),
              first(),
              map(promos => ({
                ...cart,
                promo: cart.promo.map(cp => {
                  const prm = promos.promosEcommerce.filter(pr => pr.ClavePromo === cp.ClavePromo)[0];
                  return { ...prm, ...cp };
                })
              })
              )
            )
        ),
      ),
      map(this.summaryPrice)
    );

  private summaryPrice = (resp: { cartProductos: IProductos[], cartPromos: IProductos[] }): ISummaryPrice => {
    const totalProducts: IProductos[] = resp.cartProductos.concat(resp.cartPromos);
    return totalProducts.length < 1 ? SummaryPriceDefault :
      totalProducts.map(produc => ({
        total_cajas: produc.productos || 0,
        total_precio: produc.precio_original ? produc.price_info || (produc.precio || 0) * (produc.productos || 0) : (produc.precio || 0) * (produc.productos || 0),
        precio_descuento: produc.precio_original ? (produc.precio_original || 0) * (produc.productos || 0) - (produc.price_info || (produc.precio || 0) * (produc.productos || 0)) : 0,
        FactorCl: (produc.FactorCl || 0) * (produc.productos || 0),
        puntos_recompepsi: (produc.puntos_recompepsi || 0) * (produc.productos || 0),
      }))
        .map(values => ({
          total_cajas: values.total_cajas,
          total_precio: this.twoDecimal(values.total_precio),
          precio_descuento: this.twoDecimal(values.precio_descuento),
          FactorCl: values.FactorCl,
          puntos_recompepsi: values.puntos_recompepsi,
        }))
        .reduce((a, b) => ({
          total_cajas: a.total_cajas + b.total_cajas,
          total_precio: a.total_precio + b.total_precio,
          precio_descuento: a.precio_descuento + b.precio_descuento,
          FactorCl: a.FactorCl + b.FactorCl,
          puntos_recompepsi: a.puntos_recompepsi + b.puntos_recompepsi
        }));
  }

  public getSummaryPricesPromo = (promos: IPromosEcommerce[]): ISummaryPricePromo[] => {
    return promos.map(promo =>
      (promo.Productos || []).map(product => ({
        total_precio_descuento: (product.precio || 0) * (product.productos || 0),
        total_precio: (product.precio_original || 0) * (product.productos || 0),
        total_descuento: (((product.precio_original || 0) - (product.precio || 0)) * (product.productos || 0)) * (-1),
      }))
        .map(values => ({
          ClavePromo: promo.ClavePromo,
          total_precio_descuento: this.twoDecimal(values.total_precio_descuento),
          total_precio: this.twoDecimal(values.total_precio),
          total_descuento: this.twoDecimal(values.total_descuento),
        }))
        .reduce((a, b) => ({
          ClavePromo: promo.ClavePromo,
          total_precio_descuento: a.total_precio_descuento + b.total_precio_descuento,
          total_precio: a.total_precio + b.total_precio,
          total_descuento: a.total_descuento + b.total_descuento,
        })));
  }

  /**
   * Valida si cumple la suma de los productos para completar cajas fisicas
   * @param productos que filtrará para agrupar condicion
   * @returns true o false, si cumple con la division de cajas fisicas
   */
  public validarProductos = (productos: IProductos[]) => {
    return Object.values(
      productos
        .filter(p => p.Etiquetados)
        .reduce((acc: any, p) => {
          const key = p.KeyEtiquetados as string;
          if (!acc[key]) {
            acc[key] = { sumaProductos: 0, TotalEnCaja: p.TotalEnCaja };
          }
          acc[key].sumaProductos += ((p.TotalEnCaja || 0) / (p.Multiplicador || 0)) * (p.productos || 0);
          return acc;
        }, {})
    )
      .every((value: any) => value.TotalEnCaja > 0 && value.sumaProductos % value.TotalEnCaja === 0);
  }

}
